import React, {useEffect, useRef} from 'react';

import {TextSelectionPopover} from 'app/general/overlays/TextSelectionPopover';
import {UserContentContainer} from 'app/general/structures/UserContentContainer';

import {TextHighlightHoverListener} from './TextHighlightHoverListener';

import {useTextHighlights} from '../TextHighlightsContextProvider';

import type {CommentableContentType} from 'sweet-types';

type Props = {
  versionId?: number;
  objectId?: number;
  stepId: string;
  content: string | React.ReactElement<'div'> | React.ReactElement<'span'>;
  contentType: CommentableContentType;
  innerRef?:
    | React.MutableRefObject<HTMLDivElement | null>
    | ((ref: HTMLDivElement) => void);
  targetNullStepid?: boolean;
};

export const HighlightableTextWrapper = ({
  versionId,
  objectId,
  stepId,
  content,
  contentType,
  innerRef,
  targetNullStepid = false,
}: Props) => {
  if (versionId && objectId) {
    return (
      <HighlightText
        versionId={versionId}
        objectId={objectId}
        stepId={stepId}
        content={content}
        contentType={contentType}
        innerRef={innerRef}
        targetNullStepid={targetNullStepid}
      />
    );
  }

  return <UserContentContainer content={content} innerRef={innerRef} />;
};

type HighlightTextProps = Props & {
  versionId: number;
  objectId: number;
  contentType: CommentableContentType;
};

const HighlightText = ({
  versionId,
  objectId,
  stepId,
  content,
  contentType,
  innerRef,
  targetNullStepid = false,
}: HighlightTextProps) => {
  const {highlights, setCurrentVersionId} = useTextHighlights();

  useEffect(() => {
    setCurrentVersionId(versionId);
  }, [versionId]);

  const contentRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (innerRef != null && 'current' in innerRef) {
      contentRef.current = innerRef.current;
    }
  }, [innerRef]);

  const visibleTextHighlights = Object.values(highlights).filter(
    (highlight) =>
      highlight.versionId === versionId &&
      (highlight.stepId === stepId ||
        (targetNullStepid && highlight.stepId == null)) /// highlight.stepId == null is for legacy comments
  );

  return (
    <>
      {contentRef.current != null ? (
        <TextSelectionPopover
          targetRef={contentRef.current}
          objectId={objectId}
          versionId={versionId}
          stepId={stepId}
          contentType={contentType}
        />
      ) : null}
      <TextHighlightHoverListener enabled={visibleTextHighlights.length > 0}>
        <UserContentContainer
          content={content}
          innerRef={contentRef}
          visibleTextHighlights={visibleTextHighlights}
          visibleTextHighlightsKeys={visibleTextHighlights
            .map((highlight) => highlight.id)
            .join('-')}
        />
      </TextHighlightHoverListener>
    </>
  );
};
